import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LibraryCategoryDialogComponent } from '@components/editor/library/library-category-dialog/library-category-dialog.component';
import { ElementAttribute } from '@models/editor';
import { LibraryComponentService } from '@service/library-component.service';
import { LibraryComponent } from 'icreate/common/models/library-component';
import { LibraryAndCategories } from 'icreate/common/models/library';
import { LibraryCategory } from 'icreate/common/models/library-category';
import { Vessel } from 'icreate/common/models/vessel';
import { v4 as uuidv4 } from 'uuid';
import { LibraryComponentSocketService } from '@service/library-component-socket.service';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { LibraryComponentHelperService } from '@service/canvas/library-component-helper.service';
import { LibrarySocketService } from '@service/library-socket.service';
import { LibraryCategorySocketService } from '@service/library-category-socket.service';
import { AutosavePage } from '@service/auto-save-controller.service';

@Component({
    selector: 'app-create-library-component-container',
    templateUrl: './create-library-component-container.component.html',
    styleUrls: ['./create-library-component-container.component.css'],
    standalone: false,
})
export class CreateLibraryComponentContainerComponent implements OnInit, OnDestroy, OnChanges {
    @Input() vessel!: Vessel;
    @Input() isOpenAddLibraryContainer: boolean = false;
    @Input() elementSettings: Element;
    @Input() libraryList: LibraryAndCategories[] = [];
    @Input() element!: HTMLElement;
    @Input() shipyardId!: string;

    @Output() onClearCreateComponent = new EventEmitter<void>();

    @ViewChild('deleteDialog') deleteDialog: ConfirmationWindowComponent;

    libraryNameList: string[] = [];
    libraryCategoryNameList: string[] = [];
    selectedLibrary: LibraryAndCategories;
    selectedCategory: LibraryCategory;
    componentName: string = '';
    deleteMainMessage: string = '';
    isDuplicateName: boolean = false;

    selectedCategoryLibraryList: LibraryComponent[] = [];
    selectedElementComponentList: LibraryComponent[] = [];
    componentCategoryNameList: string[] = [];
    isLoading: boolean = false;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private libraryComponentService: LibraryComponentService,
        private dialog: MatDialog,
        private libraryComponentSocketService: LibraryComponentSocketService,
        private librarySocketService: LibrarySocketService,
        private libraryCategorySocketService: LibraryCategorySocketService,
        private translateService: TranslateService,
        private libraryComponentHelperService: LibraryComponentHelperService
    ) {}

    async ngOnInit() {
        this.updateLibraryNameList();

        this.librarySocketService
            .libraryCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newLibrary: LibraryAndCategories) => {
                if (newLibrary.shipyardId === this.vessel.shipyardId) {
                    this.updateLibraryNameList();
                }
            });

        this.librarySocketService
            .libraryUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedLibrary: LibraryAndCategories) => {
                if (updatedLibrary.shipyardId === this.vessel.shipyardId) {
                    this.updateLibraryNameList();
                }
            });

        this.librarySocketService
            .libraryDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(() => {
                this.updateLibraryNameList();
            });

        this.libraryCategorySocketService
            .libraryCategoryCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newLibraryCategory: LibraryCategory) => {
                if (newLibraryCategory.shipyardId === this.vessel.shipyardId) {
                    this.updateLibraryCategoryNameList();
                }
            });

        this.libraryCategorySocketService
            .libraryCategoryUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedLibraryCategory: LibraryCategory) => {
                if (updatedLibraryCategory.shipyardId === this.vessel.shipyardId) {
                    this.updateLibraryCategoryNameList();
                }
            });

        this.libraryCategorySocketService
            .libraryCategoryDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(() => {
                this.updateLibraryCategoryNameList();
            });

        this.libraryComponentSocketService
            .libraryComponentCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((newLibraryComponent: LibraryComponent) => {
                if (this.getElementComponentId() === newLibraryComponent.elementComponentId) {
                    this.selectedElementComponentList.push(newLibraryComponent);
                    this.updateCategoryNameList();
                }
            });

        this.libraryComponentSocketService
            .libraryComponentUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((updatedLibraryComponent: LibraryComponent) => {
                if (this.getElementComponentId() === updatedLibraryComponent.elementComponentId) {
                    this.selectedElementComponentList.forEach((component, index) => {
                        if (component.id === updatedLibraryComponent.id) {
                            this.selectedElementComponentList[index] = updatedLibraryComponent;
                        }
                    });
                    this.updateCategoryNameList();
                }
            });

        this.libraryComponentSocketService
            .libraryComponentDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((libraryComponentId: string) => {
                let indexToDelete = this.selectedElementComponentList.findIndex(
                    component => component.id === libraryComponentId
                );
                if (indexToDelete !== -1) {
                    this.selectedElementComponentList.splice(indexToDelete, 1);
                    this.updateCategoryNameList();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.element && this.element) {
            await this.getSelectedElementComponentList();
            this.updateCategoryNameList();
        }
    }

    selectLibrary(event: { name: string; value: string }) {
        this.selectedCategory = null;
        this.componentName = '';
        this.selectedLibrary = this.libraryList.find(library => library.name === event.value);

        this.updateLibraryCategoryNameList();
    }

    updateLibraryCategoryNameList() {
        if (this.selectedLibrary) {
            this.libraryCategoryNameList = this.selectedLibrary.libraryCategories?.map(
                libraryCategory => libraryCategory.name
            );
        } else {
            this.libraryCategoryNameList = [];
        }
    }

    updateLibraryNameList() {
        this.libraryNameList = this.libraryList ? this.libraryList.map(library => library.name) : [];
    }

    async selectCategory(event: { name: string; value: string }) {
        this.componentName = '';
        this.selectedCategory = this.selectedLibrary.libraryCategories?.find(
            libraryCategory => libraryCategory.name === event.value
        );

        if (this.selectedCategory?.id) {
            this.selectedCategoryLibraryList = await this.libraryComponentService.getByLibraryCategoryId(
                this.selectedCategory.id
            );
        }
    }

    async createComponent() {
        try {
            this.isLoading = true;
            const cloneElement = this.element.cloneNode(true) as HTMLElement;
            cloneElement.removeAttribute(ElementAttribute.id);

            let newElementComponentId = cloneElement.getAttribute(ElementAttribute.elementComponentId);

            if (!newElementComponentId) {
                newElementComponentId = uuidv4();
                cloneElement.setAttribute(ElementAttribute.elementComponentId, newElementComponentId);
            }

            this.libraryComponentHelperService.saveElementPosition(cloneElement, this.element);

            await this.libraryComponentService.create({
                shipyardId: this.shipyardId,
                elementComponentId: newElementComponentId,
                libraryCategoryId: this.selectedCategory.id,
                name: this.componentName,
                svg: cloneElement.outerHTML,
            });

            if (newElementComponentId) {
                this.element.setAttribute(ElementAttribute.elementComponentId, newElementComponentId);
            }
            this.clearCreateComponent();
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    changeName(event: { name: string; value: string }) {
        this.componentName = event.value;
        this.isDuplicateName = this.selectedCategoryLibraryList.some(
            libraryComponent => libraryComponent.name === this.componentName
        );
    }

    async clearCreateComponent() {
        this.selectedLibrary = undefined;
        this.selectedCategory = undefined;
        this.componentName = '';
        this.libraryCategoryNameList = [];

        await this.getSelectedElementComponentList();
        this.updateCategoryNameList();

        this.onClearCreateComponent.emit();
    }

    isCreateButtonDisabled() {
        return !(this.selectedLibrary && this.selectedCategory && this.componentName && !this.isDuplicateName);
    }

    openNewLibraryDialog() {
        this.dialog.open(LibraryCategoryDialogComponent, {
            data: {
                libraryList: this.libraryList,
                vessel: this.vessel,
                library: undefined,
            },
        });
    }

    async deleteComponent(libraryComponent: LibraryComponent) {
        try {
            this.deleteMainMessage = this.translateService.instant('editor.library.delete.main-title', {
                name: libraryComponent.name,
            });

            this.deleteDialog.setVisible(true, true, AutosavePage.MIMIC);
            const option = await lastValueFrom(this.deleteDialog.onOptionSelected.pipe(take(1)));
            if (option !== ConfirmationWindowTypes.SAVE) return false;
            this.isLoading = true;

            await this.libraryComponentService.deleteComponent(libraryComponent.id);
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    async getSelectedElementComponentList() {
        try {
            const elementComponentId = this.getElementComponentId();
            this.isLoading = true;
            if (elementComponentId) {
                const componentList = await this.libraryComponentService.getByElementComponentId(elementComponentId);
                this.selectedElementComponentList = componentList;
            } else {
                this.selectedElementComponentList = [];
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    getElementComponentId() {
        return this.element.getAttribute(ElementAttribute.elementComponentId);
    }

    updateCategoryNameList() {
        let libraryCategoryList: LibraryCategory[] = this.libraryList.reduce((prev, library) => {
            return prev.concat(library.libraryCategories);
        }, []);

        const idToName = new Map(libraryCategoryList.map(category => [category.id, category.name]));
        this.componentCategoryNameList = this.selectedElementComponentList
            .map(setting => idToName.get(setting.libraryCategoryId))
            .filter((n): n is string => !!n);
    }
}
